import { useEffect, useState, useRef } from "react";
import cx from "classnames";
import { CardElement, RiskDataCollector } from "@recurly/react-recurly";

import ErrorSwitch from "../../../../../../ErrorSwitch";
import t from "../../../../../../../utils/translate";

import styles from "./styles.module.scss";

/**
 *
 * @param {object} props
 * @param {boolean} props.isCardElementTouched
 * @param {boolean} props.isCheckoutBtnTouched
 * @param {function} props.setIsCardElementValid
 * @param {function} props.setIsCardElementTouched
 * @returns
 */
const CreditCard = ({
  isCardElementTouched,
  isCheckoutBtnTouched,
  setIsCardElementValid,
  setIsCardElementTouched
}) => {
  const validationRules = {
    isInvalidNumberType: false,
    isRequiredNumber: false,
    isInvalidNumber: false,
    isRequiredExpiry: false,
    isInvalidExpiry: false,
    isRequiredCVV: false,
    isInvalidCVV: false
  };

  const [inputFocus, setInputFocus] = useState(false);
  const [validations, setValidations] = useState(validationRules);
  const [showError, setShowError] = useState(false);
  const isValidInput = !Object.values(validations).some(v => v);
  const inputRef = useRef(null);

  const errorMessageHandler = ev => {
    setValidations(validationRules);

    switch (true) {
      case !ev.number.valid:
        if (ev.number.empty) {
          setValidations({ ...validationRules, isRequiredNumber: true });
        } else {
          setValidations({ ...validationRules, isInvalidNumber: true });
        }
        break;
      case !ev.expiry.valid:
        if (ev.expiry.empty) {
          setValidations({ ...validationRules, isRequiredExpiry: true });
        } else {
          setValidations({ ...validationRules, isInvalidExpiry: true });
        }
        break;
      case !ev.cvv.valid:
        if (ev.cvv.empty) {
          setValidations({ ...validationRules, isRequiredCVV: true });
        } else {
          setValidations({ ...validationRules, isInvalidCVV: true });
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isCardElementTouched) {
      setValidations({
        isRequiredNumber: true,
        isRequiredExpiry: true,
        isRequiredCVV: true
      });
    }
  }, []);

  useEffect(() => {
    if (isCardElementTouched) {
      setIsCardElementValid(isValidInput);
    }
  }, [isValidInput, validations]);

  useEffect(() => {
    if (isCheckoutBtnTouched && !isCardElementTouched) {
      setShowError(true);
      inputRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [isCheckoutBtnTouched, showError, isValidInput]);

  const handleFocus = () => {
    setIsCardElementTouched(true);
    setShowError(false);
    setInputFocus(true);
  };

  const handleBlur = ev => {
    errorMessageHandler(ev.state);
    setShowError(true);

    setInputFocus(false);
  };

  return (
    <>
      <RiskDataCollector strategy="kount" />
      <div
        className={cx(styles.recurlyInputCtn, {
          [styles.focus]: inputFocus,
          [styles.ccError]: Boolean(showError && !isValidInput)
        })}
        ref={inputRef}
      >
        <CardElement
          className={cx(styles.recurlyInput, {})}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            fontFamily: "Roboto",
            placeholder: {
              content: "1234 1234 1234 1234"
            }
          }}
        />
        {showError && (
          <ErrorSwitch name="cc-number" validations={validations}>
            {{
              isInvalidNumberType: t("PAYMENT_CREDIT_CARD_TYPE_ERRORS_INVALID"),
              isRequiredNumber: t("PAYMENT_CREDIT_CARD_NUMBER_ERRORS_REQUIRED"),
              isInvalidNumber: t("PAYMENT_CREDIT_CARD_NUMBER_ERRORS_INVALID"),
              isRequiredExpiry: t(
                "PAYMENT_CREDIT_CARD_EXPIRATION_ERRORS_REQUIRED"
              ),
              isInvalidExpiry: t(
                "PAYMENT_CREDIT_CARD_EXPIRATION_ERRORS_INVALID"
              ),
              isRequiredCVV: t("PAYMENT_CREDIT_CARD_CVV_ERRORS_REQUIRED"),
              isInvalidCVV: t("PAYMENT_CREDIT_CARD_CVV_ERRORS_INVALID")
            }}
          </ErrorSwitch>
        )}
      </div>
    </>
  );
};

export default CreditCard;
